import { useMutation, useQueries, useQuery } from "@tanstack/react-query";
import type {
  CreatorTriggerType,
  CreatorUpcomingAutomation,
  SequenceRunListItem,
  SequenceRunsSortColumn,
  SequencesSortColumn,
  SequenceTriggerBindingInput,
  VideoPolicyRunsItem,
} from "@unbox/api";
import { SEQUENCE_RUNS_NEGATABLE_FILTERS } from "@unbox/api";
import { useCallback } from "react";
import { handleError } from "@/api/error";
import type { Pagination } from "@/api/pagination";
import type { ChunkResponse } from "@/hooks/use-progressive-fetch";
import { toNotQuery } from "@/providers/filter-registry";
import { queryClient } from "@/providers/QueryClientProviderWrapper";
import { toQuery } from "./pagination";
import { client, type MerchantScope, useScopedMerchantClient } from "./scoped-merchant-client";

export const useSequences = (opts?: { type?: "creator" | "safety" }) => {
  const scope = useScopedMerchantClient();
  const type = opts?.type;

  return useQuery({
    queryKey: ["sequences", scope.merchantId, scope.workspaceId, type],
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].sequences.$get({
            param: scope.param,
            query: {
              ...(type ? { type } : {}),
            },
          })
        : await client.merchant[":merchantId"].sequences.$get({
            param: scope.param,
            query: {
              ...(type ? { type } : {}),
            },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId,
  });
};

type SequencesListOptions = {
  pagination: Pagination;
  search?: string;
  sort?: { column: SequencesSortColumn; direction: "asc" | "desc" };
};

export const usePaginatedSequences = (options: SequencesListOptions) => {
  const scope = useScopedMerchantClient();
  const { pagination, search, sort } = options;

  return useQuery({
    queryKey: [
      "sequences",
      scope.merchantId,
      scope.workspaceId,
      "paginated",
      pagination.offset,
      pagination.limit,
      search,
      sort?.column,
      sort?.direction,
    ],
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].sequences.$get({
            param: scope.param,
            query: {
              ...toQuery(pagination),
              ...(search ? { search } : {}),
              ...(sort?.column ? { column: sort.column, direction: sort.direction } : {}),
            },
          })
        : await client.merchant[":merchantId"].sequences.$get({
            param: scope.param,
            query: {
              ...toQuery(pagination),
              ...(search ? { search } : {}),
              ...(sort?.column ? { column: sort.column, direction: sort.direction } : {}),
            },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId,
  });
};

export const useSequencesCount = (opts?: { search?: string }) => {
  const scope = useScopedMerchantClient();
  const search = opts?.search;

  return useQuery({
    queryKey: ["sequences-count", scope.merchantId, scope.workspaceId, search],
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].sequences.count.$get({
            param: scope.param,
            query: {
              ...(search ? { search } : {}),
            },
          })
        : await client.merchant[":merchantId"].sequences.count.$get({
            param: scope.param,
            query: {
              ...(search ? { search } : {}),
            },
          });
      if (!res.ok) throw await handleError(res);
      return (await res.json()).total;
    },
    enabled: !!scope.merchantId,
  });
};

export const useSequence = (sequenceId: string, opts?: { enabled?: boolean }) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["sequences", scope.merchantId, scope.workspaceId, sequenceId],
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].sequences[":id"].$get({
            param: { ...scope.param, id: sequenceId },
          })
        : await client.merchant[":merchantId"].sequences[":id"].$get({
            param: { ...scope.param, id: sequenceId },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: (opts?.enabled ?? true) && !!scope.merchantId && !!sequenceId,
  });
};

/**
 * Steps for several sequences at once. The trigger-binding endpoint names each bound sequence but
 * not its steps, and the builder needs every scope's flow in memory to offer "copy steps from".
 */
export const useSequencesByIds = (sequenceIds: string[]) => {
  const scope = useScopedMerchantClient();

  return useQueries({
    queries: sequenceIds.map((sequenceId) => ({
      queryKey: ["sequences", scope.merchantId, scope.workspaceId, sequenceId],
      queryFn: async () => {
        const res = scope.workspaceId
          ? await client.workspace[":workspaceId"].merchant[":merchantId"].sequences[":id"].$get({
              param: { ...scope.param, id: sequenceId },
            })
          : await client.merchant[":merchantId"].sequences[":id"].$get({
              param: { ...scope.param, id: sequenceId },
            });
        if (!res.ok) throw await handleError(res);
        return await res.json();
      },
      enabled: !!scope.merchantId,
    })),
  });
};

export const useCreateSequence = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async (input: {
      name: string;
      type?: "creator" | "safety";
      triggerType?: "safety_action" | "collaboration_invite" | "sample_delivered" | "creator_dormant";
      steps?: StepInput[];
    }) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].sequences.$post({
            param: scope.param,
            json: input,
          })
        : await client.merchant[":merchantId"].sequences.$post({
            param: scope.param,
            json: input,
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sequences", scope.merchantId, scope.workspaceId] });
    },
  });
};

// Full step shape accepted by the sequence PUT/POST endpoints. Linear callers
// (the step builder) only set send_message / remove_from_collab and omit the
// branch fields; branched safety policies round-trip every field via the
// optional members so no node is dropped on save.
type StepInput = {
  step: number;
  type:
    | "send_message"
    | "remove_from_collab"
    | "remove_from_linked_collab"
    | "remove_from_gmv_max"
    | "add_to_gmv_max_campaign"
    | "block_creator"
    | "notify_tiktok"
    | "branch"
    | "resolve_risk";
  delayDays: number;
  sendAtHour: number | null;
  sendAtTimezone: string | null;
  messageTemplateId: string | null;
  customMessage: string | null;
  branch?: "taken" | "not_taken" | null;
  branchCondition?: "video_removed" | "metadata_edited" | null;
  enabled?: boolean;
};

/**
 * Creates the sequence when `sequenceId` is null, otherwise replaces its name and steps. The
 * builder saves several flows in one action — a base plus one per cohort variation — so it needs
 * a mutation it can call in a loop rather than one bound to a single id at hook level.
 */
export const useUpsertSequence = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async ({
      sequenceId,
      name,
      steps,
    }: {
      sequenceId: string | null;
      name: string;
      steps: StepInput[];
    }): Promise<string> => {
      if (sequenceId) {
        const res = scope.workspaceId
          ? await client.workspace[":workspaceId"].merchant[":merchantId"].sequences[":id"].$put({
              param: { ...scope.param, id: sequenceId },
              json: { name, steps },
            })
          : await client.merchant[":merchantId"].sequences[":id"].$put({
              param: { ...scope.param, id: sequenceId },
              json: { name, steps },
            });
        if (!res.ok) throw await handleError(res);
        return sequenceId;
      }

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].sequences.$post({
            param: scope.param,
            json: { name, type: "creator" as const, steps },
          })
        : await client.merchant[":merchantId"].sequences.$post({
            param: scope.param,
            json: { name, type: "creator" as const, steps },
          });
      if (!res.ok) throw await handleError(res);
      return (await res.json()).id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sequences", scope.merchantId, scope.workspaceId] });
    },
  });
};

export const useRenameSequence = (sequenceId: string) => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async (name: string) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].sequences[":id"].$patch({
            param: { ...scope.param, id: sequenceId },
            json: { name },
          })
        : await client.merchant[":merchantId"].sequences[":id"].$patch({
            param: { ...scope.param, id: sequenceId },
            json: { name },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sequences", scope.merchantId, scope.workspaceId] });
      queryClient.invalidateQueries({ queryKey: ["sequence-defaults", scope.merchantId, scope.workspaceId] });
    },
  });
};

export const useUpdateSequence = (sequenceId: string) => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async (input: { name?: string; steps?: StepInput[] }) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].sequences[":id"].$put({
            param: { ...scope.param, id: sequenceId },
            json: input,
          })
        : await client.merchant[":merchantId"].sequences[":id"].$put({
            param: { ...scope.param, id: sequenceId },
            json: input,
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sequences", scope.merchantId, scope.workspaceId] });
    },
  });
};

export const useDeleteSequence = (sequenceId: string) => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].sequences[":id"].$delete({
            param: { ...scope.param, id: sequenceId },
          })
        : await client.merchant[":merchantId"].sequences[":id"].$delete({
            param: { ...scope.param, id: sequenceId },
          });
      if (!res.ok) throw await handleError(res);
    },
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ["sequences", scope.merchantId, scope.workspaceId, sequenceId] });
      queryClient.invalidateQueries({ queryKey: ["sequences", scope.merchantId, scope.workspaceId], exact: true });
    },
  });
};

type RunsListOptions = {
  pagination: Pagination;
  sequenceId?: string;
  status?: string;
  search?: string;
  not?: string[];
  sort?: { column: SequenceRunsSortColumn; direction: "asc" | "desc" };
};

const sequenceRunsKey = (scope: MerchantScope, options: RunsListOptions) => [
  "sequence-runs",
  scope.merchantId,
  scope.workspaceId,
  options.sequenceId,
  options.status,
  options.search,
  options.not?.toString(),
  options.sort?.column,
  options.sort?.direction,
  options.pagination.limit,
  options.pagination.offset,
];

const fetchSequenceRuns = async (scope: MerchantScope, options: RunsListOptions) => {
  const { pagination, sequenceId, status, search, not, sort } = options;
  const notQuery = toNotQuery(not, SEQUENCE_RUNS_NEGATABLE_FILTERS);
  const query = {
    ...toQuery(pagination),
    ...(sequenceId ? { sequenceId } : {}),
    ...(status ? { status } : {}),
    ...(search ? { search } : {}),
    ...(notQuery ? { not: notQuery } : {}),
    ...(sort?.column ? { column: sort.column, direction: sort.direction } : {}),
  };
  const res = scope.workspaceId
    ? await client.workspace[":workspaceId"].merchant[":merchantId"].sequences.runs.$get({ param: scope.param, query })
    : await client.merchant[":merchantId"].sequences.runs.$get({ param: scope.param, query });
  if (!res.ok) throw await handleError(res);
  return await res.json();
};

export const useSequenceRuns = (options: RunsListOptions) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: sequenceRunsKey(scope, options),
    queryFn: () => fetchSequenceRuns(scope, options),
    enabled: !!scope.merchantId,
  });
};

export const useSequenceRun = (runId: string | null) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["sequence-run", scope.merchantId, scope.workspaceId, runId],
    queryFn: async () => {
      if (!runId) throw new Error("Missing runId");
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].sequences.runs[":runId"].$get({
            param: { ...scope.param, runId },
          })
        : await client.merchant[":merchantId"].sequences.runs[":runId"].$get({
            param: { ...scope.param, runId },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId && !!runId,
  });
};

const BULK_CANCEL_CHUNK_SIZE = 500;

export const useBulkCancelSequenceRuns = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async (runIds: string[]) => {
      let cancelled = 0;
      for (let i = 0; i < runIds.length; i += BULK_CANCEL_CHUNK_SIZE) {
        const json = { runIds: runIds.slice(i, i + BULK_CANCEL_CHUNK_SIZE) };
        const res = scope.workspaceId
          ? await client.workspace[":workspaceId"].merchant[":merchantId"].sequences.runs["bulk-cancel"].$post({
              param: scope.param,
              json,
            })
          : await client.merchant[":merchantId"].sequences.runs["bulk-cancel"].$post({ param: scope.param, json });
        if (!res.ok) throw await handleError(res);
        cancelled += (await res.json()).cancelled;
      }
      return { cancelled };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sequence-runs", scope.merchantId, scope.workspaceId] });
      queryClient.invalidateQueries({ queryKey: ["sequence-runs-count", scope.merchantId, scope.workspaceId] });
      queryClient.invalidateQueries({ queryKey: ["sequence-run", scope.merchantId, scope.workspaceId] });
    },
  });
};

export const useCancelSequenceRun = (runId: string | null) => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async () => {
      if (!runId) throw new Error("Missing runId");
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].sequences.runs[":runId"].cancel.$post({
            param: { ...scope.param, runId },
          })
        : await client.merchant[":merchantId"].sequences.runs[":runId"].cancel.$post({
            param: { ...scope.param, runId },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sequence-run", scope.merchantId, scope.workspaceId, runId] });
      queryClient.invalidateQueries({ queryKey: ["sequence-runs", scope.merchantId, scope.workspaceId] });
      queryClient.invalidateQueries({ queryKey: ["sequence-runs-count", scope.merchantId, scope.workspaceId] });
    },
  });
};

const POLICY_RUNS_CHUNK_SIZE = 1000;

export const useVideoPolicyRuns = (videoIds: string[], opts?: { enabled?: boolean }) => {
  const scope = useScopedMerchantClient();
  const key = [...videoIds].sort().join(",");

  return useQuery({
    queryKey: ["video-policy-runs", scope.merchantId, scope.workspaceId, key],
    queryFn: async () => {
      const chunks: VideoPolicyRunsItem[][] = [];
      for (let i = 0; i < videoIds.length; i += POLICY_RUNS_CHUNK_SIZE) {
        const json = { videoIds: videoIds.slice(i, i + POLICY_RUNS_CHUNK_SIZE) };
        const res = scope.workspaceId
          ? await client.workspace[":workspaceId"].merchant[":merchantId"].sequences.runs["policy-runs"].$post({
              param: scope.param,
              json,
            })
          : await client.merchant[":merchantId"].sequences.runs["policy-runs"].$post({ param: scope.param, json });
        if (!res.ok) throw await handleError(res);
        chunks.push(await res.json());
      }
      return chunks.flat();
    },
    enabled: (opts?.enabled ?? true) && !!scope.merchantId && videoIds.length > 0,
  });
};

type RunsCountOptions = { sequenceId?: string; status?: string; search?: string; not?: string[] };

const sequenceRunsCountKey = (scope: MerchantScope, opts?: RunsCountOptions) => [
  "sequence-runs-count",
  scope.merchantId,
  scope.workspaceId,
  opts?.sequenceId,
  opts?.status,
  opts?.search,
  opts?.not?.toString(),
];

const fetchSequenceRunsCount = async (scope: MerchantScope, opts?: RunsCountOptions) => {
  const notQuery = toNotQuery(opts?.not, SEQUENCE_RUNS_NEGATABLE_FILTERS);
  const query = {
    ...(opts?.sequenceId ? { sequenceId: opts.sequenceId } : {}),
    ...(opts?.status ? { status: opts.status } : {}),
    ...(opts?.search ? { search: opts.search } : {}),
    ...(notQuery ? { not: notQuery } : {}),
  };
  const res = scope.workspaceId
    ? await client.workspace[":workspaceId"].merchant[":merchantId"].sequences.runs.count.$get({
        param: scope.param,
        query,
      })
    : await client.merchant[":merchantId"].sequences.runs.count.$get({ param: scope.param, query });
  if (!res.ok) throw await handleError(res);
  const data = await res.json();
  return data.total;
};

export const useSequenceRunsCount = (opts?: RunsCountOptions) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: sequenceRunsCountKey(scope, opts),
    queryFn: () => fetchSequenceRunsCount(scope, opts),
    enabled: !!scope.merchantId,
  });
};

export const useSequenceRunRowsFetcher = (opts: Omit<RunsListOptions, "pagination">) => {
  const scope = useScopedMerchantClient();

  return useCallback(
    async (offset: number, limit: number): Promise<ChunkResponse<SequenceRunListItem>> => {
      const options = { ...opts, pagination: { limit, offset } };
      const countOpts = { sequenceId: opts.sequenceId, status: opts.status, search: opts.search, not: opts.not };
      const [items, total] = await Promise.all([
        queryClient.fetchQuery({
          queryKey: sequenceRunsKey(scope, options),
          queryFn: () => fetchSequenceRuns(scope, options),
          staleTime: Number.POSITIVE_INFINITY,
        }),
        queryClient.fetchQuery({
          queryKey: sequenceRunsCountKey(scope, countOpts),
          queryFn: () => fetchSequenceRunsCount(scope, countOpts),
          staleTime: Number.POSITIVE_INFINITY,
        }),
      ]);
      return { items, total };
    },
    [scope, opts],
  );
};

// --- Creator Triggers ---

export const useSequenceTriggers = () => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["sequence-triggers", scope.merchantId, scope.workspaceId],
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].sequences.triggers.$get({
            param: scope.param,
          })
        : await client.merchant[":merchantId"].sequences.triggers.$get({
            param: scope.param,
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId,
  });
};

export const useSequenceTriggerBinding = (triggerType: CreatorTriggerType | null) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["sequence-trigger-binding", scope.merchantId, scope.workspaceId, triggerType],
    queryFn: async () => {
      if (!triggerType) throw new Error("Missing triggerType");
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].sequences.triggers[":triggerType"].$get({
            param: { ...scope.param, triggerType },
          })
        : await client.merchant[":merchantId"].sequences.triggers[":triggerType"].$get({
            param: { ...scope.param, triggerType },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId && !!triggerType,
  });
};

export const useSaveSequenceTriggerBinding = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async ({
      triggerType,
      ...input
    }: SequenceTriggerBindingInput & { triggerType: CreatorTriggerType }) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].sequences.triggers[":triggerType"].$put({
            param: { ...scope.param, triggerType },
            json: input,
          })
        : await client.merchant[":merchantId"].sequences.triggers[":triggerType"].$put({
            param: { ...scope.param, triggerType },
            json: input,
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sequence-triggers", scope.merchantId, scope.workspaceId] });
      queryClient.invalidateQueries({ queryKey: ["sequence-trigger-binding", scope.merchantId, scope.workspaceId] });
      queryClient.invalidateQueries({ queryKey: ["sequence-defaults", scope.merchantId, scope.workspaceId] });
    },
  });
};

export const useSequenceTemplates = () => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["sequence-templates", scope.merchantId, scope.workspaceId],
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].sequences.templates.$get({
            param: scope.param,
          })
        : await client.merchant[":merchantId"].sequences.templates.$get({
            param: scope.param,
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId,
  });
};

/**
 * Creators a trigger's automation currently reaches: every merchant creator minus the members of
 * the excluded cohorts. Cohorts with their own variation are not subtracted — they are still
 * reached, just by a different sequence.
 */
export const useSequenceImpactPreview = (triggerType: CreatorTriggerType | null, excludedCohortIds: string[]) => {
  const scope = useScopedMerchantClient();
  const key = [...excludedCohortIds].sort().join(",");

  return useQuery({
    queryKey: ["sequence-impact-preview", scope.merchantId, scope.workspaceId, triggerType, key],
    queryFn: async () => {
      if (!triggerType) throw new Error("Missing triggerType");
      const json = { triggerType, excludedCohortIds };
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].sequences["impact-preview"].$post({
            param: scope.param,
            json,
          })
        : await client.merchant[":merchantId"].sequences["impact-preview"].$post({
            param: scope.param,
            json,
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId && !!triggerType,
  });
};

// --- Sequence Defaults ---

type SequenceDefault = {
  triggerType: string;
  sequenceId: string;
  sequenceName: string;
  enabled: boolean;
};

export const useSequenceDefaults = () => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["sequence-defaults", scope.merchantId, scope.workspaceId],
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].sequences.defaults.$get({
            param: scope.param,
          })
        : await client.merchant[":merchantId"].sequences.defaults.$get({
            param: scope.param,
          });
      if (!res.ok) throw await handleError(res);
      return (await res.json()) as SequenceDefault[];
    },
    enabled: !!scope.merchantId,
  });
};

export const useSetSequenceDefault = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async ({
      triggerType,
      sequenceId,
      applyToExisting,
    }: {
      triggerType: string;
      sequenceId: string;
      /** Clears every existing collaboration override for this trigger in the same request. */
      applyToExisting?: boolean;
    }) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].sequences.defaults[":triggerType"].$put({
            param: { ...scope.param, triggerType: triggerType as "safety_action" },
            json: { sequenceId, ...(applyToExisting ? { applyToExisting: true } : {}) },
          })
        : await client.merchant[":merchantId"].sequences.defaults[":triggerType"].$put({
            param: { ...scope.param, triggerType: triggerType as "safety_action" },
            json: { sequenceId, ...(applyToExisting ? { applyToExisting: true } : {}) },
          });
      if (!res.ok) throw await handleError(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sequence-defaults", scope.merchantId, scope.workspaceId] });
      queryClient.invalidateQueries({
        queryKey: ["collaboration-sequence-overrides", scope.merchantId, scope.workspaceId],
      });
    },
  });
};

export const useDeleteAutomation = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async (triggerType: CreatorTriggerType) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].sequences.triggers[":triggerType"].$delete({
            param: { ...scope.param, triggerType },
          })
        : await client.merchant[":merchantId"].sequences.triggers[":triggerType"].$delete({
            param: { ...scope.param, triggerType },
          });
      if (!res.ok) throw await handleError(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sequence-defaults", scope.merchantId, scope.workspaceId] });
      queryClient.invalidateQueries({ queryKey: ["sequence-triggers", scope.merchantId, scope.workspaceId] });
      queryClient.invalidateQueries({ queryKey: ["sequence-trigger-binding", scope.merchantId, scope.workspaceId] });
      queryClient.invalidateQueries({ queryKey: ["sequences", scope.merchantId, scope.workspaceId] });
    },
  });
};

export const useDeleteSequenceDefault = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async (triggerType: string) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].sequences.defaults[":triggerType"].$delete({
            param: { ...scope.param, triggerType: triggerType as "safety_action" },
          })
        : await client.merchant[":merchantId"].sequences.defaults[":triggerType"].$delete({
            param: { ...scope.param, triggerType: triggerType as "safety_action" },
          });
      if (!res.ok) throw await handleError(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sequence-defaults", scope.merchantId, scope.workspaceId] });
      queryClient.invalidateQueries({ queryKey: ["sequence-triggers", scope.merchantId, scope.workspaceId] });
      queryClient.invalidateQueries({ queryKey: ["sequence-trigger-binding", scope.merchantId, scope.workspaceId] });
    },
  });
};

export const useCreatorUpcomingAutomations = (creatorId: string) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["creator-upcoming-automations", scope.merchantId, scope.workspaceId, creatorId],
    queryFn: async (): Promise<CreatorUpcomingAutomation[]> => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].sequences.runs.upcoming.$get({
            param: scope.param,
            query: { creatorId },
          })
        : await client.merchant[":merchantId"].sequences.runs.upcoming.$get({
            param: scope.param,
            query: { creatorId },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId && !!creatorId,
  });
};
